import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { GraduationCap, BookOpen, Video, Users, Check } from 'lucide-react';

export default function HomePage() {
  const features = [
    {
      icon: BookOpen,
      title: 'Corsi on-demand',
      description: 'Accedi a centinaia di corsi video sempre disponibili',
    },
    {
      icon: Video,
      title: 'Live streaming',
      description: 'Partecipa a lezioni dal vivo con i migliori docenti',
    },
    {
      icon: Users,
      title: 'Community',
      description: 'Connettiti con studenti e insegnanti di tutto il mondo',
    },
  ];

  const benefits = [
    'Certificati riconosciuti',
    'Materiali scaricabili',
    'Supporto 24/7',
    'Accesso mobile e desktop',
    'Progresso tracciato',
    'Pagamenti sicuri',
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-slate-50">
      <header className="sticky top-0 z-50 w-full bg-white/80 backdrop-blur-sm border-b border-gray-200">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center">
            <GraduationCap className="w-8 h-8 text-blue-600" />
            <span className="ml-2 text-xl font-bold text-gray-900">LMS Platform</span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/login">
              <Button variant="ghost">Accedi</Button>
            </Link>
            <Link to="/signup">
              <Button>Registrati</Button>
            </Link>
          </div>
        </div>
      </header>

      <main>
        <section className="py-12 md:py-20 px-4">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center space-y-6">
              <h1 className="text-4xl md:text-6xl font-bold text-gray-900 leading-tight">
                Impara e insegna
                <span className="block text-blue-600">ovunque, in qualsiasi momento</span>
              </h1>
              <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto">
                La piattaforma LMS completa per corsi online, live streaming e gestione dell'apprendimento
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                <Link to="/signup">
                  <Button size="lg" className="w-full sm:w-auto text-lg px-8">
                    Inizia gratis
                  </Button>
                </Link>
                <Link to="/dashboard/courses">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto text-lg px-8">
                    Esplora corsi
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-20 px-4 bg-white">
          <div className="container mx-auto max-w-6xl">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              Tutto ciò di cui hai bisogno
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <Card key={index} className="border-2 hover:border-blue-600 transition-colors">
                    <CardHeader>
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                        <Icon className="w-6 h-6 text-blue-600" />
                      </div>
                      <CardTitle className="text-xl">{feature.title}</CardTitle>
                      <CardDescription className="text-base">
                        {feature.description}
                      </CardDescription>
                    </CardHeader>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-20 px-4">
          <div className="container mx-auto max-w-4xl">
            <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-8">
                Perché scegliere LMS Platform?
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-lg text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-20 px-4 bg-blue-600">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Pronto per iniziare?
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Unisciti a migliaia di studenti e insegnanti sulla nostra piattaforma
            </p>
            <Link to="/signup">
              <Button size="lg" variant="secondary" className="text-lg px-8">
                Registrati ora
              </Button>
            </Link>
          </div>
        </section>
      </main>

      <footer className="bg-gray-900 text-gray-300 py-8 px-4">
        <div className="container mx-auto max-w-6xl text-center">
          <div className="flex items-center justify-center mb-4">
            <GraduationCap className="w-6 h-6 text-blue-500" />
            <span className="ml-2 text-lg font-semibold">LMS Platform</span>
          </div>
          <p className="text-sm">© 2024 LMS Platform. Tutti i diritti riservati.</p>
        </div>
      </footer>
    </div>
  );
}
